import { Button, Heading, HFlow, Icon, Text, Tooltip, VFlow } from 'bold-ui'
import { usePecField } from 'components/form'
import { confirm } from 'components/modals/confirm'
import { NavigationButton } from 'components/route/NavigationButton'
import { MChatRiscoEnum } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { isEmpty } from 'lodash'
import React from 'react'
import { MetaPath } from 'util/metaPath'

import { MChatRfAccordionSection, MChatRfAccordionSectionProps } from '../../components/MChatRfAccordionSection'
import { MChatRfAccordionStepper } from '../../components/MChatRfAccordionStepper'
import { MChatRfAccordionSectionEnum } from '../../model-mChatRf'
import { convertMChatToPerguntaRespostaInput } from '../converter-mchat'
import { downloadMChat } from '../impressao/downloadMChat'
import { MChatFieldModel } from '../model-mchat'

interface MChatFieldAccordionSectionProps
  extends Omit<MChatRfAccordionSectionProps, 'section' | 'value' | 'stepper' | 'primaryActions' | 'secondaryActions'> {
  name: MetaPath<MChatFieldModel>
  valuesAtendimentoAnterior: MChatFieldModel
  atendimentoAnteriorAtendProfId: ID
  modalUrl: string
  onDelete: () => void
}

export function MChatFieldAccordionSection(props: Readonly<MChatFieldAccordionSectionProps>) {
  const { name, valuesAtendimentoAnterior, modalUrl, onDelete, atendimentoAnteriorAtendProfId, ...sectionProps } = props

  const {
    atendimentoProfissional: { id: atendProfId },
    cidadao: { id: cidadaoId },
  } = useAtendimentoContext()

  const {
    input: { value },
  } = usePecField({ name, subscription: { value: true } })

  const hasDadosAtendimentoAtual = !isEmpty(value)
  const hasDadosAtendimentoAnterior = !isEmpty(valuesAtendimentoAnterior)

  const isRiscoMedio = hasDadosAtendimentoAtual
    ? value?.risco === MChatRiscoEnum.MEDIO
    : valuesAtendimentoAnterior?.risco === MChatRiscoEnum.MEDIO
  const isConsultaSeguimentoAtiva = (hasDadosAtendimentoAtual || hasDadosAtendimentoAnterior) && isRiscoMedio
  const isReavaliar = hasDadosAtendimentoAnterior && !hasDadosAtendimentoAtual
  const hideReavaliar = isReavaliar && valuesAtendimentoAnterior?.risco === MChatRiscoEnum.ELEVADO
  const mChatToPrint = hasDadosAtendimentoAtual ? value : valuesAtendimentoAnterior

  const handlePrint = () => {
    downloadMChat({
      atendProfId: hasDadosAtendimentoAtual ? atendProfId : atendimentoAnteriorAtendProfId,
      cidadaoId: cidadaoId,
      resultado: mChatToPrint.resultado,
      risco: mChatToPrint.risco,
      aplicacao: convertMChatToPerguntaRespostaInput(mChatToPrint),
    })
  }

  const handleDelete = () => confirmDeleteDialog(onDelete)

  return (
    <MChatRfAccordionSection
      section={MChatRfAccordionSectionEnum.MCHAT}
      value={hasDadosAtendimentoAtual ? value : valuesAtendimentoAnterior}
      isConsultaSeguimentoAtiva={isConsultaSeguimentoAtiva}
      stepper={
        <MChatRfAccordionStepper
          showPendingIcon={!hasDadosAtendimentoAtual && !hasDadosAtendimentoAnterior}
          showCheckIcon={hasDadosAtendimentoAtual || hasDadosAtendimentoAnterior}
          showConnector={isRiscoMedio}
          showExclamationIcon={false}
        />
      }
      primaryActions={
        !hasDadosAtendimentoAtual &&
        !hideReavaliar && (
          <NavigationButton kind={!isReavaliar ? 'primary' : 'normal'} size='small' to={modalUrl}>
            {isReavaliar ? 'Reavaliar' : 'Avaliar'}
          </NavigationButton>
        )
      }
      secondaryActions={
        <HFlow hSpacing={0.5}>
          {(hasDadosAtendimentoAnterior || hasDadosAtendimentoAtual) && (
            <Tooltip text='Imprimir'>
              <Button type='button' kind='normal' skin='ghost' size='small' onClick={handlePrint}>
                <Icon icon='printerOutline' />
              </Button>
            </Tooltip>
          )}
          {hasDadosAtendimentoAtual && (
            <HFlow hSpacing={0.5}>
              <Tooltip text='Editar'>
                <NavigationButton
                  type='button'
                  kind='normal'
                  skin='ghost'
                  size='small'
                  disabled={!hasDadosAtendimentoAtual}
                  to={`${modalUrl}/editar`}
                >
                  <Icon icon='penOutline' />
                </NavigationButton>
              </Tooltip>
              <Tooltip text='Excluir'>
                <Button type='button' kind='normal' skin='ghost' size='small' onClick={handleDelete}>
                  <Icon icon='trashOutline' />
                </Button>
              </Tooltip>
            </HFlow>
          )}
        </HFlow>
      }
      hasCurrentValue={!!value}
      {...sectionProps}
    />
  )
}

const confirmDeleteDialog = (handleConfirm: () => void) => {
  confirm({
    confirmLabel: 'Excluir',
    cancelLabel: 'Cancelar',
    type: 'danger',
    size: 'auto',
    onConfirm: handleConfirm,
    render: () => (
      <HFlow>
        <Icon icon='exclamationTriangleOutline' fill='danger' size={3} />
        <VFlow vSpacing={0.125}>
          <Heading level={1}>Deseja excluir a avaliação?</Heading>
          <Text>As informações registradas e o resultado do M-CHAT-R™ serão perdidos.</Text>
        </VFlow>
      </HFlow>
    ),
  })()
}
